import {
  DASHBOARD_ARTIFACT_SPEC_V2_VERSION,
  type AnyDashboardArtifactSpec,
  type DashboardAdHocDataset,
  type DashboardArtifactSpec,
  type DashboardArtifactSpecV2,
  type DashboardWidget,
  type DashboardWidgetV2,
} from "@t3tools/contracts";

/**
 * Upgrades a stored spec to v2 so v1 versions keep working: every v1 dataset
 * becomes an ad-hoc dataset over the raw replica (v1 SQL reads the analytics
 * replica directly), and every data widget reads it through
 * `{ kind: "dataset", dataset }`. A v2 spec is returned as is.
 *
 * Each dataset's required plain-language definition names the widgets it
 * feeds and repeats the definitions they cite.
 */
export function upgradeSpecV1toV2(spec: AnyDashboardArtifactSpec): DashboardArtifactSpecV2 {
  if (spec.specVersion === DASHBOARD_ARTIFACT_SPEC_V2_VERSION) return spec;
  const widgets = spec.rows.flatMap((row) => row.widgets);
  return {
    specVersion: DASHBOARD_ARTIFACT_SPEC_V2_VERSION,
    title: spec.title,
    ...(spec.description !== undefined ? { description: spec.description } : {}),
    parameters: spec.parameters,
    datasets: spec.datasets.map((dataset) => upgradeDataset(spec, dataset, widgets)),
    rows: spec.rows.map((row) => ({ ...row, widgets: row.widgets.map(upgradeWidget) })),
    definitions: spec.definitions,
  };
}

function upgradeWidget(widget: DashboardWidget): DashboardWidgetV2 {
  if (widget.kind === "text") return widget;
  const { dataset, ...rest } = widget;
  return { ...rest, data: { kind: "dataset", dataset } } as DashboardWidgetV2;
}

const MAX_DEFINITION_LENGTH = 2_000;

function upgradeDataset(
  spec: DashboardArtifactSpec,
  dataset: DashboardArtifactSpec["datasets"][number],
  widgets: ReadonlyArray<DashboardWidget>,
): DashboardAdHocDataset {
  const readers = widgets.filter(
    (widget) => widget.kind !== "text" && widget.dataset === dataset.id,
  );
  const cited = [
    ...new Set(
      readers.flatMap((widget) =>
        widget.kind === "text" || widget.definition === undefined ? [] : [widget.definition],
      ),
    ),
  ].flatMap((id) => {
    const definition = spec.definitions.find((candidate) => candidate.id === id);
    return definition ? [`${definition.term}: ${definition.text}`] : [];
  });
  const titles = readers.map((widget) => `'${widget.title}'`);
  const intro =
    titles.length > 0
      ? `Ad-hoc SQL behind ${titles.join(", ")}; not a verified metric.`
      : `Ad-hoc SQL "${dataset.id}" from the original dashboard; not a verified metric.`;
  return {
    id: dataset.id,
    source: "raw_replica",
    definition: [intro, ...cited].join(" ").slice(0, MAX_DEFINITION_LENGTH),
    sql: dataset.sql,
    bind: dataset.bind,
    columns: dataset.columns,
  };
}
